import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  APIClient,
  setAuthorization,
  getLoggedinUser,
} from "../helpers/api_helper";
import * as url from "../helpers/url_helper";

const api = new APIClient();

let state = {
  status: null,
  loading: false,
  error: null,
  loadedAt: 0,
  tick: 0,
};
const listeners = new Set();

function notify() {
  listeners.forEach((l) => l());
}

function setState(patch) {
  state = { ...state, ...patch };
  notify();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

function ensureAuth() {
  const u = getLoggedinUser();
  if (u?.access_token) setAuthorization(u.access_token);
}

let inflight = null;

async function refresh() {
  if (inflight) return inflight;
  ensureAuth();
  setState({ loading: true, error: null });
  inflight = (async () => {
    try {
      const r = await api.get(url.GET_TT_STATUS);
      const inner = r?.data ?? r;
      const status = inner?.data ?? inner;
      setState({ status, loading: false, loadedAt: Date.now() });
      return status;
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "Error de conexión";
      setState({ loading: false, error: msg });
      throw e;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

async function clockIn() {
  ensureAuth();
  setState({ error: null });
  try {
    await api.create(url.POST_CLOCK_IN, {});
    await refresh();
  } catch (e) {
    const msg = typeof e === "string" ? e : e?.message || "No se pudo marcar entrada";
    setState({ error: msg });
    throw e;
  }
}

async function clockOut() {
  ensureAuth();
  setState({ error: null });
  try {
    await api.create(url.POST_CLOCK_OUT, {});
    await refresh();
  } catch (e) {
    const msg = typeof e === "string" ? e : e?.message || "No se pudo marcar salida";
    setState({ error: msg });
    throw e;
  }
}

async function startBreak() {
  ensureAuth();
  setState({ error: null });
  try {
    await api.create(url.POST_BREAK_START, {});
    await refresh();
  } catch (e) {
    const msg = typeof e === "string" ? e : e?.message || "No se pudo iniciar la pausa";
    setState({ error: msg });
    throw e;
  }
}

async function endBreak() {
  ensureAuth();
  setState({ error: null });
  try {
    await api.create(url.POST_BREAK_END, {});
    await refresh();
  } catch (e) {
    const msg = typeof e === "string" ? e : e?.message || "No se pudo finalizar la pausa";
    setState({ error: msg });
    throw e;
  }
}

let tickRefs = 0;
let tickIntervalId = null;
function acquireTick() {
  tickRefs += 1;
  if (tickIntervalId == null) {
    tickIntervalId = setInterval(() => {
      setState({ tick: state.tick + 1 });
    }, 1000);
  }
  return () => {
    tickRefs -= 1;
    if (tickRefs <= 0 && tickIntervalId != null) {
      clearInterval(tickIntervalId);
      tickIntervalId = null;
      tickRefs = 0;
    }
  };
}

let pollRefs = 0;
let pollIntervalId = null;
function acquirePoll() {
  pollRefs += 1;
  if (pollIntervalId == null) {
    pollIntervalId = setInterval(() => {
      refresh().catch(() => {});
    }, 60000);
  }
  return () => {
    pollRefs -= 1;
    if (pollRefs <= 0 && pollIntervalId != null) {
      clearInterval(pollIntervalId);
      pollIntervalId = null;
      pollRefs = 0;
    }
  };
}

export function deriveJornadaState(status) {
  if (!status) return "off";
  if (status.is_active && status.is_on_break) return "paused";
  if (status.is_active) return "working";
  if ((status.entries || []).length > 0) return "finished";
  return "off";
}

export function workedSeconds(status) {
  let sec = 0;
  if (Array.isArray(status?.entries)) {
    for (const e of status.entries) {
      if (e.duration_minutes != null) sec += Number(e.duration_minutes) * 60;
    }
  }
  if (status?.is_active && status?.active_clock_in_at) {
    const start = new Date(status.active_clock_in_at).getTime();
    const stop = status?.is_on_break && status?.current_break?.start_at
      ? new Date(status.current_break.start_at).getTime()
      : Date.now();
    sec += Math.max(0, Math.floor((stop - start) / 1000));
  }
  for (const b of (status?.breaks || [])) {
    if (!b?.end_at) continue;
    const s = new Date(b.start_at).getTime();
    const e = new Date(b.end_at).getTime();
    if (!Number.isNaN(s) && !Number.isNaN(e)) {
      sec -= Math.max(0, Math.floor((e - s) / 1000));
    }
  }
  return Math.max(0, Math.round(sec));
}

export function breakSeconds(status) {
  if (!status?.is_on_break || !status?.current_break?.start_at) return 0;
  const start = new Date(status.current_break.start_at).getTime();
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}

export function useTimeTracking({ poll = true, tick = false } = {}) {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (state.loadedAt === 0 && !state.loading) {
      refresh().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!poll) return undefined;
    return acquirePoll();
  }, [poll]);

  useEffect(() => {
    if (!tick) return undefined;
    return acquireTick();
  }, [tick]);

  const doClockIn = useCallback(async () => {
    await clockIn();
  }, []);
  const doClockOut = useCallback(async () => {
    await clockOut();
  }, []);
  const doStartBreak = useCallback(async () => {
    await startBreak();
  }, []);
  const doEndBreak = useCallback(async () => {
    await endBreak();
  }, []);
  const doRefresh = useCallback(async () => {
    await refresh();
  }, []);

  return {
    status: snapshot.status,
    loading: snapshot.loading,
    error: snapshot.error,
    jornadaState: deriveJornadaState(snapshot.status),
    seconds: workedSeconds(snapshot.status),
    breakSeconds: breakSeconds(snapshot.status),
    clockIn: doClockIn,
    clockOut: doClockOut,
    startBreak: doStartBreak,
    endBreak: doEndBreak,
    refresh: doRefresh,
  };
}
